import React from 'react';
import { TrendingUp, TrendingDown, Minus, Download, FileText, AlertTriangle, Inbox } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { pctChange } from '@/lib/format.js';

// App-level dashboard idioms (rebuilt on the registry Card/Badge/Button — these are
// app components, not registry primitives). Mirrors opd-app Analytics.jsx's
// KpiCard/ChartCard/Delta/DonutCenter shapes.

export const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
export const CHART_FILL = (i) => `hsl(var(--chart-${(i % 5) + 1}))`;

export function Delta({ now, prev, goodWhenDown = false, suffix = 'vs prev' }) {
  const pct = pctChange(now, prev);
  if (pct === null) return null;
  if (Math.round(pct) === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
        <Minus className="size-3" /> {suffix}
      </span>
    );
  }
  const up = pct > 0;
  const good = goodWhenDown ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${good ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--allergy))]'}`}>
      <Icon className="size-3" />
      {Math.abs(Math.round(pct))}% {suffix}
    </span>
  );
}

export function KpiCard({ label, value, sub, now, prev, goodWhenDown, tone, icon: Icon }) {
  return (
    <Card className="min-w-0 flex-1 gap-2 py-4">
      <CardContent className="flex flex-col gap-1.5 px-4">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {Icon && <Icon className="size-3.5" />}{label}
        </div>
        <div className={`text-[23px] font-semibold leading-none tabular-nums ${tone || 'text-foreground'}`}>{value}</div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {(now !== undefined && prev !== undefined) && <Delta now={now} prev={prev} goodWhenDown={goodWhenDown} />}
          {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

export function ChartCard({ title, subtitle, headline, headlineSub, delta, empty, emptyText, action, className, children }) {
  return (
    <Card className={`min-w-0 gap-3 py-4 ${className || ''}`}>
      <CardContent className="flex flex-col gap-3 px-4">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-[13.5px]">{title}</CardTitle>
            {subtitle && <CardDescription className="mt-0.5 text-[11.5px]">{subtitle}</CardDescription>}
          </div>
          {action}
        </div>
        {(headline !== undefined && headline !== null) && (
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span className="text-[24px] font-semibold leading-none tabular-nums text-foreground">{headline}</span>
            {delta && <Delta now={delta.now} prev={delta.prev} goodWhenDown={delta.goodWhenDown} />}
            {headlineSub && <span className="text-[11.5px] text-muted-foreground">{headlineSub}</span>}
          </div>
        )}
        {empty ? (
          <div className="py-8 text-center text-[12.5px] text-muted-foreground">{emptyText || 'Nothing in this range yet.'}</div>
        ) : children}
      </CardContent>
    </Card>
  );
}

// Center label overlay for a donut (recharts leaves the hole empty).
export function DonutCenter({ primary, secondary }) {
  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
      <span className="text-[16px] font-semibold leading-none tabular-nums text-foreground">{primary}</span>
      {secondary && <span className="mt-1 text-[10.5px] text-muted-foreground">{secondary}</span>}
    </div>
  );
}

// The "unpriced" signal — never a silent ₹0 (contract §3).
export function UnpricedBadge({ qty, count }) {
  return (
    <Badge
      variant="outline"
      title="No price on file for this usage — set a rate on the Pricing screen"
      className="h-5 gap-1 border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-bg))] px-1.5 text-[10px] font-semibold text-[hsl(var(--ai-foreground))]"
    >
      <AlertTriangle className="size-3" /> unpriced{qty !== undefined ? ` · ${count}` : ''}
    </Badge>
  );
}

export function MockBadge() {
  return (
    <Badge
      variant="outline"
      title="Serving deterministic in-memory mock data — no backend connected"
      className="h-6 gap-1.5 border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-bg))] px-2 text-[11px] font-bold tracking-wide text-[hsl(var(--ai-foreground))]"
    >
      <span className="size-1.5 rounded-full bg-current" /> MOCK DATA
    </Badge>
  );
}

// Empty-meter state — the "meter goes live when OPS_* env is set" copy.
export function EmptyMeter({ title = 'No usage recorded yet', sub = 'The meter goes live when OPS_* env is set on an instance.', icon: Icon = Inbox }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed py-16 text-center">
      <Icon className="size-6 text-muted-foreground" />
      <div className="text-[14px] font-semibold text-foreground">{title}</div>
      <div className="max-w-sm text-[12.5px] text-muted-foreground">{sub}</div>
    </div>
  );
}

// Export dropdown — CSV + plain-text report of the current view (present on every
// screen). Callers pass the two builders.
export function ExportMenu({ onCsv, onTxt, disabled }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Download className="size-3.5" /> Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onCsv}><Download className="size-4" /> Download CSV</DropdownMenuItem>
        <DropdownMenuItem onClick={onTxt}><FileText className="size-4" /> Download report (.txt)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ScreenHeader({ title, subtitle, children }) {
  return (
    <div className="mb-5 flex flex-wrap items-end gap-3">
      <div className="min-w-0 flex-1">
        <h1 className="text-[22px] font-bold leading-tight tracking-tight text-foreground">{title}</h1>
        {subtitle && <div className="mt-1 text-[13px] text-muted-foreground">{subtitle}</div>}
      </div>
      <div className="flex flex-none items-center gap-2">{children}</div>
    </div>
  );
}

// Honest note when some spend couldn't be converted to INR (no fx rate on file).
export function UnconvertibleNote({ currencies }) {
  if (!currencies || !currencies.length) return null;
  return (
    <div className="mb-4 flex items-center gap-2 rounded-lg border border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-bg))] px-3.5 py-2 text-[12px] font-medium text-[hsl(var(--ai-foreground))]">
      <AlertTriangle className="size-4" />
      No FX rate on file for {currencies.join(', ')} → those amounts are excluded from the INR total (raw per-currency subtotals still shown). Add a rate on the Pricing screen.
    </div>
  );
}
